import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ModalOptions } from "@ionic/angular";
import { ModalDocumentTypeComponent } from "../modals/modal-document-type/modal-document-type.component";
import { ModalService } from "@services/modal/modal.service";
import { TramitePensionService } from "../../services/tramite-pension.service";
import { TramiteStepperService } from "../../services/tramite-stepper.service";
import { ROUTING_TRAMITE_PENSION } from "@constants/url.constant";
import { StorageService } from "@services/storage/storage.service";
import { PersonalData } from '../../../shared/common/interfaces/personal.interface';
import { Beneficiary } from "@shared/common/interfaces/beneficiario.interface";
import { firstValueFrom } from "rxjs";
import { GtmService } from "@services/gtm/gtm.service";

@Component({
  selector: "app-document-upload",
  templateUrl: "./document-upload.component.html",
  styleUrls: ["./document-upload.component.scss"],
  standalone: false
})
export class DocumentUploadComponent implements OnInit {
  private readonly CODIGOS_PARENTEZCO_EXCLUIDOS_BENEFICIARIOS: number[] = [
    10, 11, 12, 13, 14, 15, 16, 22, 23, 24, 25, 95
  ];

  private readonly CATEGORIAS_ERROR_SERVICIO: Array<{
    prioridad: number;
    palabrasClaves: string[];
    mensaje: string;
  }> = [
      {
        prioridad: 1,
        palabrasClaves: ['calidad', 'legible', 'extraer datos', 'campos requeridos'],
        mensaje: 'El documento no es legible. Por favor, usa una imagen más nítida e intenta nuevamente.',
      },
      {
        prioridad: 2,
        palabrasClaves: ['recurso no se encuentra', 'Document AI'],
        mensaje: 'Hubo un error al cargar este documento. Por favor, inténtalo nuevamente.',
      },
      {
        prioridad: 3,
        palabrasClaves: ['no coincide con los datos'],
        mensaje: 'El documento no coincide con los datos registrados. Por favor, revísalo e intenta nuevamente.',
      },
    ];

  errorSize = false;
  typeError = false;
  typeFile: string;
  isCancelQuery: boolean = false;
  loadingPage: boolean = true;

  status: any = {
    nacimiento: "without_uploading_document",
    "nacimiento-conyuge": "without_uploading_document",
    matrimonio: "without_uploading_document",
    "union-civil": "without_uploading_document",
    "cedula-de-identidad": "without_uploading_document",
  };

  errorMessages: Record<string, string> = {};

  public conyuge: boolean = false;
  public convivienteCivil: boolean = false;
  public beneficiarios: Beneficiary[] = [];
  public beneficiaryDocumentKeys: string[] = [];

  constructor(
    private readonly _modalService: ModalService,
    private readonly activatedRoute: ActivatedRoute,
    readonly router: Router,
    private readonly tramitePension: TramitePensionService,
    private readonly tramiteStepper: TramiteStepperService,
    private readonly storageService: StorageService,
    private readonly gtmService: GtmService,
  ) { }

  ngOnInit(): void {
    void this.inicializarVista();
  }

  private async inicializarVista(): Promise<void> {
    try {
      this.tramiteStepper.setStepCurrent(5);
      await this.evaluaHabilitarCertificadosSegunEstadoCivil();
      await this.evaluaHabilitarCertificadosSegunBeneficiarios();
      this.inicializarStatusDesdeStorage();
    } finally {
      this.loadingPage = false;
    }
  }

  goBack() {
    this.router.navigate([`../${ROUTING_TRAMITE_PENSION.VIA_PAGO}`], {
      relativeTo: this.activatedRoute,
    });
  }

  async openModalDocType(): Promise<void> {
    const data = {
      title: "Tipos de documento",
      closeButton: () => this._modalService.closeModal(),
    };
    const opcionesModal: ModalOptions = {
      component: ModalDocumentTypeComponent,
      backdropDismiss: true,
      cssClass: "generic-modal",
      componentProps: { data },
    };
    await this._modalService.openModal(opcionesModal);
  }

  onFileSelected(payload: any): void {
    const documentTypeKey = payload.documentType;
    const apiDocumentType = this.obtenerTipoDocumentoApi(documentTypeKey);
    const beneficiaryRut = this.obtenerRutBeneficiarioPorTipoDocumento(documentTypeKey);
    this.isCancelQuery = false;
    this.errorMessages[documentTypeKey] = "";
    this.status[documentTypeKey] = "VALIDATION_IN_PROGRESS";

    this.tramitePension.uploadDocument({
      ...payload,
      documentType: apiDocumentType,
      ...(beneficiaryRut ? { beneficiaryRut } : {}),
    }).subscribe({
      next: (response: any) => {
        const id = response?.data;
        this.status[documentTypeKey] = response?.status === 201 ? "VALIDATION_IN_PROGRESS" : null;
        setTimeout(() => this.recursiveDataFetch(documentTypeKey, apiDocumentType, id), 3000);
      },
      error: (err: any) => {
        if (this.isCancelQuery) {
          this.status[documentTypeKey] = "without_uploading_document";
        } else {
          this.status[documentTypeKey] = "ERROR";
          this.errorMessages[documentTypeKey] =
            'El documento no coincide con los datos registrados. Por favor, revísalo e intenta nuevamente.';
        }
      },
    });
  }

  recursiveDataFetch(documentTypeKey: string, apiDocumentType: string, id: string): void {
    this.tramitePension.getDocumentStatus(apiDocumentType, id).subscribe({
      next: (response: any) => {
        const { status } = response.data;
        if (status === "VALIDATION_IN_PROGRESS" && !this.isCancelQuery) {
          this.status[documentTypeKey] = status;
          setTimeout(() => this.recursiveDataFetch(documentTypeKey, apiDocumentType, id), 3000);
        } else {
          this.status[documentTypeKey] = status;
          if (status === "SUCCESS") {
            this.errorMessages[documentTypeKey] = "";
            sessionStorage.setItem(documentTypeKey, "SUCCESS");
          }
          if (status === "ERROR") {
            this.errorMessages[documentTypeKey] = this.obtenerMensajeErrorDesdeServicio(response?.data?.errors);
          }
        }
      },
      error: (error) => {
        console.error("Error al obtener el servico", error);
      }
    });
  }

  setDocumentType(type: string) {
    this.typeFile = type;
  }

  async continue() {
    try {
      this.gtmService.trackClick('button', 'Continuar', 'boton_principal', '/pensions/tramite-pensiones/solicitud/confirmar-datos');
      await this.router.navigate([`../${ROUTING_TRAMITE_PENSION.CONFIRMAR_DATOS}`], {
        relativeTo: this.activatedRoute,
      });
    } catch (error) {
      console.error("Error al navegar:", error);
    }
  }

  cancelQuery(event: boolean, documentType: string): void {
    this.isCancelQuery = event;
    this.status[documentType] = "without_uploading_document";
    this.errorMessages[documentType] = "";
  }

  delDocument(payload: any): void {
    this.status[payload.documentType] = 'without_uploading_document';
    this.errorMessages[payload.documentType] = "";
    sessionStorage.setItem(payload.documentType, "without_uploading_document");
  }

  get btnDisable(): boolean {
    const documentosBaseCompletos =
      this.status.nacimiento === "SUCCESS" &&
      this.status["cedula-de-identidad"] === "SUCCESS";

    const documentosConyugalesCompletos =
      (!this.conyuge && !this.convivienteCivil) ||
      (
        this.status["nacimiento-conyuge"] === "SUCCESS" &&
        (!this.conyuge || this.status.matrimonio === "SUCCESS") &&
        (!this.convivienteCivil || this.status["union-civil"] === "SUCCESS")
      );

    const documentosBeneficiariosCompletos = this.beneficiaryDocumentKeys.every(
      (key) => this.status[key] === "SUCCESS"
    );

    return !(documentosBaseCompletos && documentosConyugalesCompletos && documentosBeneficiariosCompletos);
  }

  private async evaluaHabilitarCertificadosSegunEstadoCivil() {
    let personalData: PersonalData = await this.storageService.get('tp_personal_data');
    if (personalData.codigoEstadoCivil === 'C') {
      this.conyuge = true;
    } else if (personalData.codigoEstadoCivil === 'P') {
      this.convivienteCivil = true;
    } else {
      this.conyuge = false;
      this.convivienteCivil = false;
    }
  }

  private async evaluaHabilitarCertificadosSegunBeneficiarios(): Promise<void> {
    try {
      const beneficiarios = await firstValueFrom(this.tramitePension.getBeneficiaries());
      this.beneficiarios = (beneficiarios ?? []).filter(
        (beneficiario) =>
          !this.CODIGOS_PARENTEZCO_EXCLUIDOS_BENEFICIARIOS.includes(
            Number(beneficiario.codigoParentesco)
          )
      );
      this.beneficiaryDocumentKeys = this.beneficiarios.map((_, index) => `nacimiento-beneficiario-${index + 1}`);
      this.beneficiaryDocumentKeys.forEach((key) => {
        this.status[key] = "without_uploading_document";
      });
    } catch {
      this.beneficiarios = [];
      this.beneficiaryDocumentKeys = [];
    }
  }

  private inicializarStatusDesdeStorage(): void {
    Object.keys(this.status).forEach((clave) => {
      const response = sessionStorage.getItem(clave);
      this.status[clave] = response ?? "without_uploading_document";
      if (!response) {
        sessionStorage.setItem(clave, "without_uploading_document");
      }
    });
  }

  private obtenerTipoDocumentoApi(documentTypeKey: string): string {
    if (documentTypeKey.startsWith("nacimiento-beneficiario-")) {
      return "nacimiento-beneficiario";
    }
    return documentTypeKey;
  }

  private obtenerRutBeneficiarioPorTipoDocumento(documentTypeKey: string): string | null {
    if (!documentTypeKey.startsWith("nacimiento-beneficiario-")) {
      return null;
    }

    const beneficiaryIndex = Number(documentTypeKey.split("nacimiento-beneficiario-")[1]) - 1;
    if (Number.isNaN(beneficiaryIndex) || beneficiaryIndex < 0) {
      return null;
    }

    return this.beneficiarios[beneficiaryIndex]?.idBeneficiario ?? null;
  }

  private obtenerMensajeErrorDesdeServicio(errors: unknown): string {
    if (!Array.isArray(errors) || errors.length === 0) {
      return 'El documento no coincide con los datos registrados. Por favor, revísalo e intenta nuevamente.';
    }

    const erroresServicio = errors
      .filter((item): item is string => typeof item === "string")
      .map(err => err.toLowerCase());

    for (const categoria of this.CATEGORIAS_ERROR_SERVICIO) {
      if (erroresServicio.some(err => categoria.palabrasClaves.some(palabra => err.includes(palabra.toLowerCase())))) {
        return categoria.mensaje;
      }
    }

    return 'El documento no coincide con los datos registrados. Por favor, revísalo e intenta nuevamente.';
  }

  getNombreCompletoBeneficiario(beneficiario: Beneficiary): string {
    const nombres = [
      beneficiario.nombres,
      beneficiario.apellidoPaterno,
      beneficiario.apellidoMaterno,
    ].filter(Boolean);
    return nombres.join(" ");
  }

  abrirRegistroCivil(event: Event): void {
    event.preventDefault();

    this.gtmService.trackClick('link', 'www.registrocivil.cl', 'link_externo', 'www.registrocivil.cl');
    window.open(
      'https://www.registrocivil.cl/',
      '_blank',
      'noopener,noreferrer'
    );
  }
}
